import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { DISCHARGE_CALENDAR_ROUTES } from 'components/dischargeCalendar/constants';
import { Flags } from 'constants/flags';
import { useEnvironment } from 'context/environment';
import { useProfile } from 'context/profile';
import { ImportType } from 'models/Client';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import AdmissionsIcon from 'svg/AdmissionsIcon';
import AllPatientsIcon from 'svg/AllPatientsIcon';
import ArrowLeft from 'svg/ArrowLeft';
import ContractedHealthPlansIcon from 'svg/ContractedHealthPlansIcon';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import DischargeCalendarIcon from 'svg/DischargeCalendarIcon';
import GearIcon from 'svg/GearIcon';
import InsightsIcon from 'svg/InsightsIcon';
import PatientPortfolioIcon from 'svg/PatientPortfolioIcon';
import ReferralsIcon from 'svg/ReferralIcon';
import SummarizationIcon from 'svg/SummarizationIcon';

import AccessGuard from './shared/AccessGuard';
import SideNavLink from './SideNavLink';
import { SideNavSection } from './SideNavSection';

const SideNavbar = () => {
  const { profile } = useProfile();
  const { environment } = useEnvironment();
  const hasAdminInPatient = profile.hasFlag(Flags.AdminInPatient);
  const [isAdminView, setIsAdminView] = useState(false);
  const location = useLocation();
  const isAdminPageActive = location.pathname.startsWith('/admin');

  const showConnectPatients = (() => {
    return (
      profile.has(PERMISSIONS.patientCreate) &&
      profile.actingClient.importType === ImportType.IMPORTED_PATIENT &&
      profile.canActAsManager &&
      (profile.isAdmin || (!profile.isPhysicianTeamUser && !profile.isAffiliateUser))
    );
  })();

  const showReferrals =
    !!profile.actingClient.providedProviderTypes.length ||
    !!profile.actingClient?.clientManagedGroupTypes.some((clientGroupType) => clientGroupType.config.referral);
  const showAdmissions =
    profile.has(PRODUCT_ACCESSORS.admissionsManagementEnabled) && profile.has(PERMISSIONS.reviewAdmissionView);
  const hasHealthPlans = profile.actingClient.providedProviderTypes.some((x) => x.config.designatesAcceptedPlans);

  if (isAdminView) {
    return (
      <NavContainer>
        <AdminNavHeader onClick={() => setIsAdminView(false)}>
          <ArrowLeft />
          <span>Viewing: Admin</span>
        </AdminNavHeader>
        <AdminNavDivider />
        <AdminNavContent>
          <AccessGuard accessor={PERMISSIONS.adminClientView}>
            <SideNavLink to='/admin/clients' title='Clients' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminUserView}>
            <SideNavLink to='/admin/users' title='Users' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminGroupView}>
            <SideNavLink to='/admin/groups' title='Groups' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminGroupView}>
            <SideNavLink to='/admin/locations' title='Locations' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminGroupTypeView}>
            <SideNavLink to='/admin/group-types' title='Group Types' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminAttrView}>
            <SideNavLink to='/admin/attributes' title='Attributes' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminAIPromptView}>
            <SideNavLink
              visible={hasAdminInPatient}
              title='AI Prompts'
              to={[
                { subtitle: 'Embedded Prompts', to: '/admin/ai-prompts', permission: PERMISSIONS.adminAIPromptView },
                {
                  subtitle: 'Workspace Prompts',
                  to: '/admin/workspace-prompts',
                  permission: PERMISSIONS.adminAIPromptView,
                },
                {
                  subtitle: 'Generated Summaries',
                  to: '/admin/generated-summaries',
                  permission: PERMISSIONS.adminAIPromptView,
                },
              ]}
            />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminQuestionTemplateView}>
            <SideNavLink to='/admin/question-templates' title='Question Templates' visible={hasAdminInPatient} />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminDischargeReasonView}>
            <SideNavLink to='/admin/discharge-reasons' title='Discharge Dispositions' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminClassificationView}>
            <SideNavLink to='/admin/plans' title='Plans' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminClassificationView}>
            <SideNavLink to='/admin/plan-types' title='Plan Types' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminClassificationView}>
            <SideNavLink to='/admin/episode-types' title='Episode Types' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminFeatureFlagView}>
            <SideNavLink to='/admin/feature-flags' title='Feature Flags' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminImportedPatientView}>
            <SideNavLink to='/admin/import-history' title='Import History' />
          </AccessGuard>
          <AccessGuard accessor={PERMISSIONS.adminLinkedRecordView}>
            <SideNavLink to='/admin/shared-records' title='Shared Records' visible={hasAdminInPatient} />
          </AccessGuard>
        </AdminNavContent>
      </NavContainer>
    );
  }

  return (
    <NavContainer>
      <NavContent>
        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <SideNavSection title='Care Coordination'>
            <SideNavLink
              to='/patients'
              title='Patient Portfolio'
              icon={<PatientPortfolioIcon width={16} height={16} />}
            />
            <SideNavLink to='/all-patients' title='All Patients' icon={<AllPatientsIcon width={16} height={16} />} />
            <SideNavLink
              to='/coordinate'
              data-cy='coordinateLink'
              title='Coordinate'
              icon={<CoordinatePatientsIcon color={'currentColor'} width={16} height={16} />}
              visible={showConnectPatients}
            />
          </SideNavSection>
          <SideNavSection title='Census Management' visible={profile.has(PRODUCT_ACCESSORS.dischargeCalendarEnabled)}>
            <SideNavLink
              to={DISCHARGE_CALENDAR_ROUTES.BASE}
              title='Discharge Calendar'
              data-cy='discharge-calendar'
              icon={<DischargeCalendarIcon width={16} height={16} />}
              end={false}
            />
          </SideNavSection>
          <SideNavSection title='Transitions of Care' visible={showReferrals || showAdmissions}>
            <SideNavLink
              data-cy='admissions-link'
              to='/admissions'
              title='Admissions'
              icon={<AdmissionsIcon width={16} height={16} />}
              visible={showAdmissions}
              end={false}
            />
            <SideNavLink
              data-cy='referrals-link'
              to='/referrals'
              title='Active Referrals'
              icon={<ReferralsIcon width={16} height={16} />}
              visible={showReferrals}
            />
            <SideNavLink
              data-cy='contractedHealthPlansLink'
              to='/contracted-health-plans'
              title='Contracted Health Plans'
              icon={<ContractedHealthPlansIcon width={16} height={16} />}
              visible={hasHealthPlans}
            />
          </SideNavSection>
          <SideNavSection title='Analytics'>
            <SideNavLink to='/insights' title='Olio Insights' icon={<InsightsIcon width={16} height={16} />} />
          </SideNavSection>
        </AccessGuard>
        <SideNavSection title='Workspaces' visible={profile.has(PRODUCT_ACCESSORS.olioSummarizationEnabled)}>
          <SideNavLink
            title='Summarization'
            data-cy='summarizationLink'
            icon={<SummarizationIcon width={16} height={16} />}
            to={[
              {
                to: '/summarizations/new',
                subtitle: 'New Summary',
              },
              {
                to: '/summarizations',
                subtitle: 'History',
              },
            ]}
          />
        </SideNavSection>
        <SideNavSection title='Admin' visible={profile.isAdmin}>
          <SideNavLink
            to=''
            title='Legacy'
            icon={<GearIcon width={18} height={18} />}
            domain={environment?.ADMIN_UI_DOMAIN}
          />
          <AdminNavButton $active={isAdminPageActive} onClick={() => setIsAdminView(true)}>
            <GearIcon width={18} height={18} />
            <span className='link-title'>Admin</span>
          </AdminNavButton>
        </SideNavSection>
      </NavContent>
    </NavContainer>
  );
};

export default SideNavbar;

const NavContainer = styled.div`
  height: 100%;
  min-width: ${({ theme }) => theme.dimensions.sideNavWidth};
  background-color: ${({ theme }) => theme.colors.black};
  flex: 0;
  display: none;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
  }
`;

const NavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;

const AdminNavHeader = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-family: 'Lato';
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18px;
  cursor: pointer;
  text-align: left;
  width: 100%;
`;

const AdminNavDivider = styled.div`
  flex-shrink: 0;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.white15};
`;

const AdminNavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-top: 8px;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;

const AdminNavButton = styled.button<{ $active?: boolean }>`
  border: 2px solid transparent;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.white : theme.colors.white75)};
  font-family: 'Lato';
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 'normal')};
  line-height: 16px;
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 14px;
  width: 100%;
  text-align: left;
  ${({ $active, theme }) =>
    $active &&
    css`
      border-left-color: ${theme.colors.white};
      background-color: ${theme.colors.white15};
    `}

  & > svg,
  & > div {
    margin-right: 10px;
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }
`;
